import type { FlowDefinition, FlowStepType } from '../types/api';

/**
 * Editing a flow definition in the admin (K3 part 3): the pure logic behind
 * adding, removing and moving steps, and the client-side checks that mirror
 * core's `parseFlowDefinition` (packages/core/src/flows/types.ts) so a
 * problem shows next to its field before the save. The server still
 * validates everything; its message shows if something slips through.
 * Keep the limits below in step with core.
 */

export type Step = FlowDefinition['steps'][number];

export const END_TARGET = 'end';
export const MAX_FLOW_STEPS = 50;
export const MAX_AB_PATHS = 4;
export const MAX_COUPON_PERCENT = 50;
export const MAX_COUPON_VALID_DAYS = 30;
export const MAX_FIELD_VALUE_LENGTH = 200;
export const MAX_ALERT_SUBJECT_LENGTH = 150;
export const MAX_ALERT_BODY_LENGTH = 1000;
export const MAX_WEBHOOK_URL_LENGTH = 500;
const FIELD_KEY_PATTERN = /^[a-z][a-z0-9_]{0,39}$/;
const MANUAL_LABEL_PATTERN = /^[a-z0-9æøå-]{1,40}$/;
const AUTO_LABEL_NAMES = [
  'new_customer',
  'repeat',
  'loyal',
  'platinum',
  'vip',
  'inactive',
  'lost',
  'subscriber_only',
];
const AB_PATH_ID_PATTERN = /^[a-z0-9_-]{1,20}$/;
const EMAIL_RE = /^[^\s@<>",;]+@[^\s@<>",;]+\.[^\s@<>",;]+$/;
const LINK_PATTERNS: readonly RegExp[] = [
  /[a-z][a-z0-9+.-]*:\/\//i,
  /\b(?:mailto|tel|javascript|data):/i,
  /\bwww\./i,
  /\b[a-z0-9-]+(?:\.[a-z0-9-]+)*\.(?:com|net|org|io|dk|de|eu|co|app|dev|info|biz|me|shop|store|online|site|xyz|ly|uk|fr|nl|it|se|no|ch|at|es|us|ai|cc|tv|link|click|top|page|cloud)\b/i,
];

export const STEP_TITLES: Record<string, string> = {
  wait: 'Wait',
  condition: 'Condition',
  create_coupon: 'Create discount code',
  send_template: 'Send email',
  update_contact: 'Update contact',
  split: 'Split',
  ab_split: 'A/B split',
  end: 'End',
  internal_alert: 'Team alert',
  webhook: 'Webhook',
};

/** The step types the "add a step" picker offers. `condition` (the older stop/skip check) is not editable here, so it is not offered. */
export const ADDABLE_STEP_TYPES: FlowStepType[] = [
  'send_template',
  'wait',
  'create_coupon',
  'update_contact',
  'split',
  'ab_split',
  'internal_alert',
  'webhook',
  'end',
];

/** Whether free text holds something a mail client would turn into a link (core `textHasLink`). */
function textHasLink(text: string): boolean {
  return LINK_PATTERNS.some((re) => re.test(text));
}

/** Why a webhook URL is not acceptable, or `null` (core `webhookUrlProblem`). */
export function webhookUrlProblem(raw: string): string | null {
  if (raw.length > MAX_WEBHOOK_URL_LENGTH) {
    return `must be at most ${MAX_WEBHOOK_URL_LENGTH} characters`;
  }
  let u: URL;
  try {
    u = new URL(raw);
  } catch {
    return 'must be a valid URL';
  }
  if (u.protocol !== 'https:') return 'must start with https://';
  if (u.username !== '' || u.password !== '') return 'must not contain a user name or password';
  if (u.port !== '' && u.port !== '443') return 'must use the default https port';
  const host = u.hostname.toLowerCase().replace(/\.$/, '');
  if (host.startsWith('[') || host.includes(':') || /^[0-9.]+$/.test(host)) {
    return 'must use a host name, not an IP address';
  }
  if (
    !host.includes('.') ||
    host === 'localhost' ||
    /\.(localhost|local|internal|lan|home|corp|intranet|test|invalid|example)$/.test(host) ||
    /(^|\.)(nip|sslip|xip)\.io$/.test(host)
  ) {
    return 'must be a public host name';
  }
  return null;
}

/** `Step 3 · Wait`, the name used in selects, messages and the move/remove buttons. */
export function stepName(steps: Step[], index: number): string {
  const step = steps[index];
  return `Step ${index + 1} · ${step ? (STEP_TITLES[step.type] ?? step.type) : '?'}`;
}

/** A branch of a step: where it goes and how to point it elsewhere. */
export interface BranchRef {
  /** Human name of the branch: "Yes", "No", or the A/B path label. */
  label: string;
  target: string;
}

/** The branch targets of a step (none for steps that do not branch). */
export function branchesOf(step: Step): BranchRef[] {
  if (step.type === 'split') {
    return [
      { label: 'Yes', target: String(step.yesTo ?? '') },
      { label: 'No', target: String(step.noTo ?? '') },
    ];
  }
  if (step.type === 'ab_split' && Array.isArray(step.paths)) {
    return (step.paths as { label?: string; to?: string }[]).map((p, i) => ({
      label: p.label?.trim() ? p.label : `Path ${i + 1}`,
      target: String(p.to ?? ''),
    }));
  }
  return [];
}

function withTargets(step: Step, map: (target: string) => string): Step {
  if (step.type === 'split') {
    return { ...step, yesTo: map(String(step.yesTo ?? '')), noTo: map(String(step.noTo ?? '')) };
  }
  if (step.type === 'ab_split' && Array.isArray(step.paths)) {
    return {
      ...step,
      paths: (step.paths as { to?: string }[]).map((p) => ({ ...p, to: map(String(p.to ?? '')) })),
    };
  }
  return step;
}

/** Why a branch target is wrong, or `null`: it must be 'end' or the id of a LATER step. */
export function targetProblem(steps: Step[], from: number, target: string): string | null {
  if (target === END_TARGET) return null;
  if (!target) return 'Choose where this branch goes.';
  const to = steps.findIndex((s) => s.id === target);
  if (to === -1) return 'This branch points at a step that no longer exists.';
  if (to <= from) return 'A branch must go to a later step, or end the flow.';
  return null;
}

export interface FlowIssue {
  stepId: string;
  /** Which field the message belongs to, e.g. `to`, `yesTo`, `paths[1].percent`. */
  field: string;
  message: string;
}

function nonEmpty(v: unknown): v is string {
  return typeof v === 'string' && v.trim().length > 0;
}

/** Client mirror of `parseFlowDefinition`'s rules, as messages for the fields. Empty list: the definition would be accepted. */
export function validateFlow(def: FlowDefinition): FlowIssue[] {
  const issues: FlowIssue[] = [];
  const steps = def.steps;
  for (const [i, step] of steps.entries()) {
    const add = (field: string, message: string) =>
      issues.push({ stepId: step.id, field, message });
    switch (step.type) {
      case 'wait':
        if (typeof step.seconds !== 'number' || !(step.seconds > 0)) {
          add('seconds', 'The wait time must be longer than 0.');
        }
        break;
      case 'create_coupon': {
        const p = step.percent;
        if (typeof p !== 'number' || !Number.isInteger(p) || p < 1 || p > MAX_COUPON_PERCENT) {
          add('percent', `The discount must be a whole number from 1 to ${MAX_COUPON_PERCENT}.`);
        }
        const d = step.validDays;
        if (typeof d !== 'number' || !Number.isInteger(d) || d < 1 || d > MAX_COUPON_VALID_DAYS) {
          add('validDays', `Valid days must be a whole number from 1 to ${MAX_COUPON_VALID_DAYS}.`);
        }
        if (!nonEmpty(step.prefix)) add('prefix', 'The code prefix cannot be empty.');
        break;
      }
      case 'send_template': {
        if (!nonEmpty(step.subject)) add('subject', 'The subject line cannot be empty.');
        if (Array.isArray(step.variants)) {
          for (const v of step.variants as { language: string; subject?: string }[]) {
            if (!nonEmpty(v.subject)) {
              add(`variants.${v.language}.subject`, 'The subject line cannot be empty.');
            }
          }
        }
        break;
      }
      case 'update_contact': {
        if (step.action === 'set_field') {
          const key = typeof step.key === 'string' ? step.key : '';
          if (!key) add('key', 'Enter the field key.');
          else if (!FIELD_KEY_PATTERN.test(key)) {
            add('key', 'Must start with a lower-case letter and use a-z, 0-9 and _ (max 40).');
          }
          const value = typeof step.value === 'string' ? step.value : '';
          if (value.length > MAX_FIELD_VALUE_LENGTH) {
            add('value', `Must be at most ${MAX_FIELD_VALUE_LENGTH} characters.`);
          }
        } else if (step.action === 'add_label' || step.action === 'remove_label') {
          const label = typeof step.label === 'string' ? step.label : '';
          if (!label) add('label', 'Enter the tag.');
          else if (!MANUAL_LABEL_PATTERN.test(label)) {
            add('label', 'Must be 1-40 characters of a-z, 0-9, æøå or -.');
          } else if (AUTO_LABEL_NAMES.includes(label)) {
            add('label', `'${label}' is a reserved automatic tag name.`);
          }
        } else if (step.action !== 'suppress_inactive') {
          add('action', 'Choose what to change.');
        }
        break;
      }
      case 'split': {
        const cond = step.condition as { kind?: string; segmentId?: string } | undefined;
        if (cond?.kind === 'segment' && !nonEmpty(cond.segmentId)) {
          add('condition', 'Choose a segment.');
        }
        const yes = targetProblem(steps, i, String(step.yesTo ?? ''));
        if (yes) add('yesTo', yes);
        const no = targetProblem(steps, i, String(step.noTo ?? ''));
        if (no) add('noTo', no);
        break;
      }
      case 'ab_split': {
        const paths = Array.isArray(step.paths)
          ? (step.paths as { id: string; label?: string; percent?: number; to?: string }[])
          : [];
        if (paths.length < 2 || paths.length > MAX_AB_PATHS) {
          add('paths', `An A/B split needs 2 to ${MAX_AB_PATHS} paths.`);
        }
        let total = 0;
        const seen = new Set<string>();
        for (const [j, p] of paths.entries()) {
          if (!AB_PATH_ID_PATTERN.test(p.id ?? '') || seen.has(p.id)) {
            add(`paths[${j}].id`, 'Path ids must be unique (1-20 characters of a-z, 0-9, _ or -).');
          }
          seen.add(p.id);
          if (!nonEmpty(p.label)) add(`paths[${j}].label`, 'Give the path a name.');
          const pc = p.percent;
          if (typeof pc !== 'number' || !Number.isInteger(pc) || pc < 1 || pc > 99) {
            add(`paths[${j}].percent`, 'Must be a whole number from 1 to 99.');
          }
          total += typeof pc === 'number' ? pc : 0;
          const t = targetProblem(steps, i, String(p.to ?? ''));
          if (t) add(`paths[${j}].to`, t);
        }
        if (total !== 100) {
          add('paths', `The percentages must add up to 100 (now ${total}).`);
        }
        break;
      }
      case 'internal_alert': {
        const to = typeof step.to === 'string' ? step.to : '';
        if (!to) add('to', 'Enter the team address to alert.');
        else if (!EMAIL_RE.test(to) || to.length > 254) add('to', 'Must be an e-mail address.');
        const subject = typeof step.subject === 'string' ? step.subject : '';
        if (!subject) add('subject', 'Enter a subject.');
        else if (subject.length > MAX_ALERT_SUBJECT_LENGTH || /[\r\n]/.test(subject)) {
          add('subject', `Must be one line, at most ${MAX_ALERT_SUBJECT_LENGTH} characters.`);
        } else if (textHasLink(subject)) add('subject', 'Must not contain a link or web address.');
        const body = typeof step.body === 'string' ? step.body : '';
        if (!body) add('body', 'Enter the message.');
        else if (body.length > MAX_ALERT_BODY_LENGTH) {
          add('body', `Must be at most ${MAX_ALERT_BODY_LENGTH} characters.`);
        } else if (textHasLink(body)) add('body', 'Must not contain a link or web address.');
        break;
      }
      case 'webhook': {
        const url = typeof step.url === 'string' ? step.url : '';
        if (!url) add('url', 'Enter the https address to call.');
        else {
          const problem = webhookUrlProblem(url);
          if (problem) add('url', `The address ${problem}.`);
        }
        break;
      }
      default:
        break;
    }
  }
  if (steps.length > MAX_FLOW_STEPS) {
    issues.push({
      stepId: steps[0]?.id ?? '',
      field: 'steps',
      message: `A flow can have at most ${MAX_FLOW_STEPS} steps.`,
    });
  }
  return issues;
}

/** The issues of one step, optionally of one field. */
export function issuesFor(issues: FlowIssue[], stepId: string, field?: string): FlowIssue[] {
  return issues.filter((i) => i.stepId === stepId && (field === undefined || i.field === field));
}

function uniqueId(steps: Step[], base: string): string {
  const used = new Set(steps.map((s) => s.id));
  for (let n = 1; ; n++) {
    const id = `${base}-${n}`;
    if (!used.has(id)) return id;
  }
}

/**
 * A new step with starting values core accepts wherever a value can be
 * guessed. Nothing it does can reach a contact by itself: a mail starts
 * without a template (the flow cannot be switched on until one is chosen),
 * and the alert and webhook start empty, so the save is blocked until the
 * admin fills in where they go. `nextId` is the step it will sit in front
 * of (or 'end'), the default target of a branch.
 */
export function newStep(type: FlowStepType, steps: Step[], nextId: string): Step {
  const id = uniqueId(steps, type.replace(/_/g, '-'));
  switch (type) {
    case 'wait':
      return { id, type, seconds: 86400 };
    case 'send_template':
      return { id, type, templateId: null, subject: 'New email', purpose: 'marketing' };
    case 'create_coupon':
      return { id, type, percent: 10, validDays: 14, prefix: 'FLOW' };
    case 'update_contact':
      return { id, type, action: 'add_label', label: '' };
    case 'split':
      return {
        id,
        type,
        condition: { kind: 'ordered_since_entry' },
        yesTo: nextId,
        noTo: nextId,
      };
    case 'ab_split':
      return {
        id,
        type,
        paths: [
          { id: 'a', label: 'Version A', percent: 50, to: nextId },
          { id: 'b', label: 'Version B', percent: 50, to: nextId },
        ],
      };
    case 'internal_alert':
      return {
        id,
        type,
        to: '',
        subject: 'Flow alert',
        body: 'A contact reached {{step_id}} in {{flow_name}}.',
      };
    case 'webhook':
      return { id, type, url: '' };
    case 'end':
      return { id, type };
    default:
      return { id, type };
  }
}

/** Inserts a step of `type` so that it becomes step number `position + 1` (0 = first). Returns the new definition and the new step's id. */
export function addStep(
  def: FlowDefinition,
  type: FlowStepType,
  position: number,
): { definition: FlowDefinition; stepId: string } {
  const pos = Math.max(0, Math.min(position, def.steps.length));
  const nextId = def.steps[pos]?.id ?? END_TARGET;
  const step = newStep(type, def.steps, nextId);
  const steps = [...def.steps.slice(0, pos), step, ...def.steps.slice(pos)];
  return { definition: { steps }, stepId: step.id };
}

/** One branch that pointed at a removed step and now goes to the step after it. */
export interface Retarget {
  stepId: string;
  /** "Step 2 · Split, branch Yes" */
  description: string;
  /** Where it goes once the step is gone, numbered as the flow will then be: "Step 4 · Wait" or "the end of the flow". */
  to: string;
}

/** What removing `stepId` does to branches that pointed at it: each goes on to the step that followed it (or the end). */
export function retargetsForRemoval(def: FlowDefinition, stepId: string): Retarget[] {
  const at = def.steps.findIndex((s) => s.id === stepId);
  if (at === -1) return [];
  const after = def.steps[at + 1];
  // Numbered as it will be once the step is gone (everything after it moves up one).
  const to = after
    ? `Step ${at + 1} · ${STEP_TITLES[after.type] ?? after.type}`
    : 'the end of the flow';
  const out: Retarget[] = [];
  for (const [i, s] of def.steps.entries()) {
    for (const b of branchesOf(s)) {
      if (b.target === stepId) {
        out.push({ stepId: s.id, description: `${stepName(def.steps, i)}, branch ${b.label}`, to });
      }
    }
  }
  return out;
}

/** Why a step cannot be removed, or `null`. */
export function removeProblem(def: FlowDefinition): string | null {
  return def.steps.length <= 1 ? 'A flow needs at least one step.' : null;
}

/** Removes a step; branches that pointed at it go to the step that followed it, or end the flow. */
export function removeStep(def: FlowDefinition, stepId: string): FlowDefinition {
  const at = def.steps.findIndex((s) => s.id === stepId);
  if (at === -1) return def;
  const replacement = def.steps[at + 1]?.id ?? END_TARGET;
  const steps = def.steps
    .filter((s) => s.id !== stepId)
    .map((s) => withTargets(s, (t) => (t === stepId ? replacement : t)));
  return { steps };
}

/** Why a step cannot move one place up (`-1`) or down (`1`), or `null` when it can. A move may never leave a branch pointing backwards. */
export function moveProblem(def: FlowDefinition, index: number, dir: -1 | 1): string | null {
  const other = index + dir;
  if (other < 0) return 'Already the first step.';
  if (other >= def.steps.length) return 'Already the last step.';
  const next = swapped(def, index, other);
  for (const [i, s] of next.steps.entries()) {
    for (const b of branchesOf(s)) {
      if (b.target === END_TARGET) continue;
      const to = next.steps.findIndex((x) => x.id === b.target);
      if (to !== -1 && to <= i) {
        return `${stepName(next.steps, i)} (branch ${b.label}) would then point backwards.`;
      }
    }
  }
  return null;
}

function swapped(def: FlowDefinition, a: number, b: number): FlowDefinition {
  const steps = [...def.steps];
  const tmp = steps[a] as Step;
  steps[a] = steps[b] as Step;
  steps[b] = tmp;
  return { steps };
}

/** Moves a step one place up or down; returns the definition unchanged when `moveProblem` says no. */
export function moveStep(def: FlowDefinition, index: number, dir: -1 | 1): FlowDefinition {
  return moveProblem(def, index, dir) === null ? swapped(def, index, index + dir) : def;
}

/** Splits 100 % evenly over `n` paths (the first gets the remainder), for a new A/B path. */
export function evenPercents(n: number): number[] {
  const base = Math.floor(100 / n);
  return Array.from({ length: n }, (_, i) => (i === 0 ? 100 - base * (n - 1) : base));
}
